import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, SlidersHorizontal, LayoutGrid, List as ListIcon, X, MapPin, Calendar as CalIcon } from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TYPE_COLOR = {
  event: 'bg-mahogany text-cream',
  retreat: 'bg-burgundy text-cream',
  class: 'bg-rose-italic text-cream',
};

const atMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const fmtRange = (ev) => {
  const s = ev.start_datetime ? new Date(ev.start_datetime) : null;
  const e = ev.end_datetime ? new Date(ev.end_datetime) : null;
  if (!s) return '';
  const opt = { month: 'short', day: 'numeric' };
  if (e && atMidnight(e) > atMidnight(s)) return `${s.toLocaleDateString(undefined, opt)} – ${e.toLocaleDateString(undefined, opt)}`;
  return `${s.toLocaleDateString(undefined, opt)}, ${s.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

export const EventsCalendar = ({ items = [] }) => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [style, setStyle] = useState('grid'); // grid | agenda
  const [showFilters, setShowFilters] = useState(false);
  const [fLoc, setFLoc] = useState('');
  const [fFac, setFFac] = useState('');

  const locations = useMemo(() => [...new Set(items.map((i) => i.location).filter(Boolean))].sort(), [items]);
  const facilitators = useMemo(() => [...new Set(items.flatMap((i) => i.facilitator_names || []).filter(Boolean))].sort(), [items]);
  const years = useMemo(() => {
    const ys = items.map((i) => (i.start_datetime ? new Date(i.start_datetime).getFullYear() : null)).filter(Boolean);
    ys.push(now.getFullYear());
    return [...new Set(ys)].sort((a, b) => b - a);
  }, [items]); // eslint-disable-line

  const filtered = useMemo(() => items.filter((i) => (!fLoc || i.location === fLoc) && (!fFac || (i.facilitator_names || []).includes(fFac))), [items, fLoc, fFac]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = new Date(year, month, 1).getDay();

  const byDay = useMemo(() => {
    const map = {};
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month, daysInMonth);
    filtered.forEach((ev) => {
      if (!ev.start_datetime) return;
      const s = atMidnight(new Date(ev.start_datetime));
      const e = atMidnight(new Date(ev.end_datetime || ev.start_datetime));
      const from = s < monthStart ? monthStart : s;
      const to = e > monthEnd ? monthEnd : e;
      if (to < monthStart || from > monthEnd) return;
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        const k = dayKey(d);
        (map[k] = map[k] || []).push(ev);
      }
    });
    return map;
  }, [filtered, year, month, daysInMonth]);

  const agendaEvents = useMemo(() => {
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month, daysInMonth, 23, 59, 59);
    return filtered
      .filter((ev) => {
        if (!ev.start_datetime) return false;
        const s = new Date(ev.start_datetime);
        const e = new Date(ev.end_datetime || ev.start_datetime);
        return e >= monthStart && s <= monthEnd;
      })
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
  }, [filtered, year, month, daysInMonth]);

  const goPrev = () => { if (month === 0) { setMonth(11); setYear((y) => y - 1); } else setMonth((m) => m - 1); };
  const goNext = () => { if (month === 11) { setMonth(0); setYear((y) => y + 1); } else setMonth((m) => m + 1); };
  const activeFilters = (fLoc ? 1 : 0) + (fFac ? 1 : 0);

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday = (d) => d && year === now.getFullYear() && month === now.getMonth() && d === now.getDate();

  return (
    <div data-testid="events-calendar">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <button onClick={goPrev} data-testid="cal-prev" className="p-2 rounded-full bg-white/70 border border-rose/50 text-mahogany hover:bg-rose-soft transition-colors"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-serif-display text-2xl text-burgundy min-w-[190px] text-center" data-testid="cal-title">{MONTHS[month]} {year}</div>
          <button onClick={goNext} data-testid="cal-next" className="p-2 rounded-full bg-white/70 border border-rose/50 text-mahogany hover:bg-rose-soft transition-colors"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full bg-white/70 border border-rose/50 p-1">
            <button onClick={() => setStyle('grid')} data-testid="cal-style-grid" title="Month grid"
              className={`px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${style === 'grid' ? 'bg-mahogany text-cream' : 'text-mahogany/70'}`}><LayoutGrid className="w-4 h-4" /> Grid</button>
            <button onClick={() => setStyle('agenda')} data-testid="cal-style-agenda" title="Agenda list"
              className={`px-3 py-1.5 rounded-full text-sm inline-flex items-center gap-1.5 transition-colors ${style === 'agenda' ? 'bg-mahogany text-cream' : 'text-mahogany/70'}`}><ListIcon className="w-4 h-4" /> Agenda</button>
          </div>
          <button onClick={() => setShowFilters((v) => !v)} data-testid="cal-filter-toggle" title="Filters"
            className={`relative p-2.5 rounded-full border transition-colors ${showFilters || activeFilters ? 'bg-mahogany text-cream border-mahogany' : 'bg-white/70 border-rose/50 text-mahogany hover:bg-rose-soft'}`}>
            <SlidersHorizontal className="w-4 h-4" />
            {activeFilters > 0 && <span className="absolute -top-1 -right-1 bg-rose-italic text-cream text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{activeFilters}</span>}
          </button>
        </div>
      </div>

      {/* Filter panel (hidden behind the icon) */}
      {showFilters && (
        <div className="mb-5 p-4 rounded-2xl bg-white/70 border border-rose/50 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cal-filters">
          <label className="text-sm">
            <span className="block text-[11px] uppercase tracking-widest text-mahogany/60 mb-1">Location</span>
            <select value={fLoc} onChange={(e) => setFLoc(e.target.value)} data-testid="cal-filter-location" className="w-full bg-cream rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany">
              <option value="">All locations</option>
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-[11px] uppercase tracking-widest text-mahogany/60 mb-1">Facilitator</span>
            <select value={fFac} onChange={(e) => setFFac(e.target.value)} data-testid="cal-filter-facilitator" className="w-full bg-cream rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany">
              <option value="">All facilitators</option>
              {facilitators.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-[11px] uppercase tracking-widest text-mahogany/60 mb-1">Year</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="cal-filter-year" className="w-full bg-cream rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany">
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-[11px] uppercase tracking-widest text-mahogany/60 mb-1">Month</span>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="cal-filter-month" className="w-full bg-cream rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany">
              {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
          </label>
          {activeFilters > 0 && (
            <button onClick={() => { setFLoc(''); setFFac(''); }} data-testid="cal-filter-clear" className="col-span-2 md:col-span-4 justify-self-start text-xs text-mahogany/70 hover:text-burgundy inline-flex items-center gap-1"><X className="w-3 h-3" /> Clear filters</button>
          )}
        </div>
      )}

      {/* Month grid */}
      {style === 'grid' ? (
        <div className="rounded-2xl overflow-hidden border border-rose/40 bg-white/50">
          <div className="grid grid-cols-7 bg-rose-soft/50">
            {WK.map((w) => <div key={w} className="px-2 py-2 text-center text-[11px] uppercase tracking-widest text-mahogany/70">{w}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, idx) => {
              const evs = d ? (byDay[dayKey(new Date(year, month, d))] || []) : [];
              return (
                <div key={idx} className={`min-h-[104px] border-t border-l border-rose/30 p-1.5 ${idx % 7 === 6 ? 'border-r' : ''} ${d ? '' : 'bg-cream/40'}`}>
                  {d && (
                    <div className={`text-xs mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday(d) ? 'bg-mahogany text-cream' : 'text-mahogany/60'}`}>{d}</div>
                  )}
                  <div className="space-y-1">
                    {evs.slice(0, 3).map((ev) => (
                      <Link key={ev.id + idx} to={`/programs/${ev.slug}`} data-testid={`cal-event-${ev.slug}`}
                        className={`block truncate rounded-md px-1.5 py-0.5 text-[11px] leading-tight ${TYPE_COLOR[ev.type] || 'bg-mahogany text-cream'} hover:opacity-90 transition-opacity`}
                        title={ev.title}>{ev.title}</Link>
                    ))}
                    {evs.length > 3 && <div className="text-[10px] text-mahogany/60 px-1">+{evs.length - 3} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Agenda */
        <div className="space-y-3" data-testid="cal-agenda">
          {agendaEvents.length === 0 ? (
            <div className="text-center text-mahogany/60 py-12">No programs in {MONTHS[month]} {year}.</div>
          ) : agendaEvents.map((ev) => (
            <Link key={ev.id} to={`/programs/${ev.slug}`} data-testid={`agenda-event-${ev.slug}`}
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/70 border border-rose/40 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className={`shrink-0 w-2 h-12 rounded-full ${TYPE_COLOR[ev.type] || 'bg-mahogany'}`} />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-widest text-mahogany/60 capitalize">{ev.type}</div>
                <div className="font-serif-display text-lg text-burgundy truncate">{ev.title}</div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-burgundy/80 mt-0.5">
                  <span className="inline-flex items-center gap-1"><CalIcon className="w-3.5 h-3.5 text-rose-italic" /> {fmtRange(ev)}</span>
                  {ev.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-rose-italic" /> {ev.location}</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mt-4 text-xs text-mahogany/70">
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-mahogany" /> Event</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-burgundy" /> Retreat</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-rose-italic" /> Class</span>
      </div>
    </div>
  );
};

export default EventsCalendar;
